import React, { useCallback, useEffect, useRef, useState } from "react";
import { UploadCloud, ImageIcon, AlertCircle } from "lucide-react";
import { AnimatedModal, ModalHeader, ModalBody } from "components/common/modals/AnimatedModal";
import { makeToast } from "helpers/common";
import defaultAvatar from "assets/images/auth/defaultAvatar.png";
import { useProfilePictureUpload } from "./hooks/useProfilePictureUpload";
import {
  ALLOWED_IMAGE_MIME_TYPES,
  MAX_IMAGE_FILE_SIZE_BYTES,
  ALLOWED_IMAGE_ACCEPT,
} from "components/common/imageUploadConstraints";
import "./ProfilePictureModal.css";

interface ProfilePictureModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentImageUrl?: string;
  onUploadSuccess: (fileUrl: string) => void;
}

const ProfilePictureModal: React.FC<ProfilePictureModalProps> = ({
  isOpen,
  onClose,
  currentImageUrl,
  onUploadSuccess,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { upload, progress, uploading, error, reset } = useProfilePictureUpload();
  const [dragActive, setDragActive] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const localPreviewRef = useRef<string | null>(null);

  useEffect(() => {
    localPreviewRef.current = localPreview;
  }, [localPreview]);

  // Revoke any outstanding object URL on unmount to avoid leaking memory
  useEffect(() => {
    return () => {
      if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
    };
  }, []);

  const setPreview = useCallback((url: string | null) => {
    if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
    setLocalPreview(url);
  }, []);

  const handleClose = useCallback(() => {
    if (uploading) return;
    reset();
    setPreview(null);
    setValidationError(null);
    onClose();
  }, [uploading, reset, setPreview, onClose]);

  const handleFile = useCallback(
    async (file?: File) => {
      if (!file) return;
      setValidationError(null);

      if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) {
        setValidationError("Invalid file type. Please use JPEG, PNG, WEBP, or HEIC.");
        return;
      }
      if (file.size > MAX_IMAGE_FILE_SIZE_BYTES) {
        setValidationError("This file's size exceeds the 2MB limit.");
        return;
      }

      setPreview(URL.createObjectURL(file));

      const res = await upload(file);
      if (res?.isSuccessful && res.data?.fileUrl) {
        makeToast("Profile picture updated", "success", 2000);
        onUploadSuccess(res.data.fileUrl);
        setPreview(null);
        onClose();
      }
    },
    [upload, onUploadSuccess, onClose, setPreview]
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      handleFile(file);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [handleFile]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      if (uploading) return;
      const file = event.dataTransfer.files?.[0];
      handleFile(file);
    },
    [uploading, handleFile]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!uploading) setDragActive(true);
    },
    [uploading]
  );

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }, []);

  const handleBrowseClick = useCallback(() => {
    if (!uploading) fileInputRef.current?.click();
  }, [uploading]);

  const handleDropzoneKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleBrowseClick();
      }
    },
    [handleBrowseClick]
  );

  const displayError = validationError || error;

  return (
    <AnimatedModal isOpen={isOpen} onClose={handleClose} maxWidth={440} ariaLabel="Upload profile picture">
      <ModalHeader title="Update Profile Picture" onClose={handleClose} />
      <ModalBody>
        <div className="ppm-container">
          <img
            src={localPreview || currentImageUrl || defaultAvatar}
            className="ppm-preview"
            alt="Profile"
          />

          <div
            className={`ppm-dropzone${dragActive ? " ppm-dropzone--active" : ""}${uploading ? " ppm-dropzone--uploading" : ""}`}
            onClick={handleBrowseClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onKeyDown={handleDropzoneKeyDown}
            role="button"
            tabIndex={0}
            aria-label="Drag and drop an image, or press Enter to browse files"
          >
            <ImageIcon size={22} className="ppm-dropzone__icon" />
            <p className="ppm-dropzone__text">
              {uploading ? "Uploading…" : "Drag & drop an image here"}
            </p>
            {!uploading && <p className="ppm-dropzone__subtext">or use the button below</p>}
          </div>

          {uploading && (
            <div className="ppm-progress-track">
              <div className="ppm-progress-fill" style={{ width: `${progress}%` }} />
              <span className="ppm-progress-label">{progress}%</span>
            </div>
          )}

          {displayError && (
            <div className="ppm-error">
              <AlertCircle size={15} />
              <span>{displayError}</span>
            </div>
          )}

          <button type="button" className="ppm-upload-btn" onClick={handleBrowseClick} disabled={uploading}>
            <UploadCloud size={16} />
            {uploading ? "Uploading…" : "Upload Image"}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_IMAGE_ACCEPT}
            className="ppm-file-input"
            onChange={handleFileInputChange}
            tabIndex={-1}
            aria-hidden="true"
          />

          <p className="ppm-specification">
            (Images can be up to <strong>2MB</strong> in size and should be in portrait rather
            than landscape orientation.)
          </p>
        </div>
      </ModalBody>
    </AnimatedModal>
  );
};

export default ProfilePictureModal;
